import { useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext.jsx';
import { importOptional } from '../lib/importOptional.js';
import { getPendingRescoreJobs, removePendingRescoreJob, deriveRescorePendingJobAction } from '../utils/rescorePendingJobs.js';

// `services/scenarios.js` imports the app's `i18n` singleton at module scope
// (for query-param language tagging), which itself eagerly calls
// `i18n.use(initReactI18next)` on import. `ToastProvider` (where this hook is
// mounted) is imported un-mocked by several component tests that only
// partially mock `react-i18next` (e.g. NotableActivityFeed.test.jsx) — a
// static top-level import here would make merely importing ToastContext.jsx
// eagerly initialize real i18n and break those tests. Lazy dynamic import
// instead: only loaded once this hook actually has a pending job to poll,
// which only happens inside a mounted app (never during a
// renderToStaticMarkup snapshot, since effects don't run there).

const POLL_MS = 2000;

/**
 * Issue #1681: app-lifetime "check on next visit" poller for background
 * bulk-rescore jobs (#1600). Mounted once in `ToastProvider` — NOT a page
 * component — so the completion toast survives both:
 *   - a plain navigate-away from `ScenarioAuthoringPage` (the original #1681
 *     gap: `RescoreOfferModal`'s `onClose` unmounts the page and its local
 *     poll before a multi-minute drain finishes), and
 *   - a `returnTo` redirect that would otherwise bypass `ScenariosPageWorking`
 *     entirely (#1681 plan review, gemini P2 — the reason this lives in
 *     `ToastProvider` rather than a single page).
 *
 * "Check on next visit", not instant: markers written by
 * `ScenarioAuthoringPage.jsx`'s `handleRescore` (via `addPendingRescoreJob`)
 * are picked up here on mount and every `POLL_MS` after. If the app is closed
 * before a terminal status, the marker is simply left in place for the next
 * session's first tick.
 *
 * All business logic (terminal/idle/running decision, toast formatting) is
 * the pure `deriveRescorePendingJobAction` — this hook is thin glue (timer +
 * fetch + side effect), consistent with the `rescoreJobView.js` pattern.
 *
 * @param {(message:string, type:string) => void} showToast - passed directly
 *   rather than read via `useToast()`, since this hook is called from INSIDE
 *   `ToastProvider`'s own render (before its Context.Provider wraps anything,
 *   a component can't consume its own provider's value in the same pass).
 */
export function useRescoreJobPoller(showToast) {
  const { user } = useAuth();
  const userId = user?.id;
  const inFlightRef = useRef(new Set());

  useEffect(() => {
    if (!userId) return undefined;

    let cancelled = false;
    // #4231: a stale tab (chunk renamed by a deploy) cannot load the scenarios
    // service. This is a background poll with no learner action to attach a
    // toast to, so stop polling quietly; the markers stay and the next page load
    // (a reload picks up the new chunk names) resumes. Never reloads.
    let chunkFailed = false;

    const tick = async () => {
      if (chunkFailed) return;
      const jobs = getPendingRescoreJobs(userId);
      if (jobs.length === 0) return;
      let ScenariosAPI;
      try {
        ({ ScenariosAPI } = await importOptional(() => import('../services/scenarios.js'), 'scenarios-service'));
      } catch (err) {
        chunkFailed = true;
        console.warn('Rescore poll stopped: scenarios service chunk failed to load', err);
        return;
      }
      for (const job of jobs) {
        const pollKey = `${job.organizationId}:${job.scenarioId}:${job.userId}`;
        if (inFlightRef.current.has(pollKey)) continue;
        inFlightRef.current.add(pollKey);
        try {
          const status = await ScenariosAPI.getRescoreStatus(job.scenarioId);
          if (cancelled) return;
          const { action, toast } = deriveRescorePendingJobAction(status);
          if (action === 'toast-and-drop') {
            showToast(toast.message, toast.type);
            removePendingRescoreJob(job);
          } else if (action === 'drop-silent') {
            removePendingRescoreJob(job);
          }
          // action === 'keep': still running — leave the marker in place.
        } catch (err) {
          console.error('Rescore status poll failed:', err);
          // mega62 review-fix (gemini P1 / codex P2): a permanently-failing
          // marker (scenario deleted → 404, access revoked → 403) would
          // otherwise re-poll every 2s on every page indefinitely. Drop it —
          // the completion toast is moot for a scenario the user can't see.
          if (err?.status === 404 || err?.status === 403) {
            removePendingRescoreJob(job);
          }
        } finally {
          inFlightRef.current.delete(pollKey);
        }
      }
    };

    tick();
    const intervalId = setInterval(tick, POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, [userId, showToast]);
}
